import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  ArrowLeft, Shield, RefreshCw, ExternalLink, Cloud,
  AlertCircle, CheckCircle, Loader2, GitBranch, Users as UsersIcon,
  FileText, Settings as SettingsIcon, Search, Folder, Save,
  ShieldAlert, Bell, Activity, Database,
  ChevronRight, ChevronDown, X, Link2, Lock, Unlock, Clock, Play,
} from 'lucide-react';
import { formatDate, parseServerDate } from '../utils/dateFormatter';
import { FrameworkLine } from './directoryWidgetCommon';
import GroupsGovernanceWidget from './GroupsGovernanceWidget';
import PostureLayout from './posture/PostureLayout';
import GwMonitoringHealth from './GwMonitoringHealth';
import Header from './Header';
import SecurityChecksTable from './SecurityChecksTable';

// =====================================================================
// Module-level in-memory cache, keyed by workspaceId, so tab switches
// and back/forward remounts of GoogleWorkspaceDetail don't re-fetch the
// (still-warm) posture and overview payloads. Cache entries are written
// straight after a successful fetch and are good for STALE_AFTER_MS
// before the component triggers a silent background refresh.
//
// The server is already cache-backed (gw_posture_snapshots); this layer
// makes intra-session navigation feel instant.
// =====================================================================
const POSTURE_MEM_CACHE = new Map();  // workspaceId -> { posture, ts }
const OVERVIEW_MEM_CACHE = new Map(); // workspaceId -> { overview, ts }
const STALE_AFTER_MS = 5 * 60 * 1000; // 5 min: refresh in background after this

const _readCache = (map, key) => {
  const hit = map.get(key);
  if (!hit) return null;
  return { ...hit, isStale: Date.now() - hit.ts > STALE_AFTER_MS };
};
const _writeCache = (map, key, value) => {
  map.set(key, { ...value, ts: Date.now() });
};
const invalidatePostureCache = (workspaceId) => {
  POSTURE_MEM_CACHE.delete(workspaceId);
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: Cloud },
  { id: 'posture', label: 'Security Checks', icon: Shield },
  { id: 'org-units', label: 'Teams & Departments', icon: GitBranch },
  { id: 'evidence', label: 'Audit Evidence', icon: FileText },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
];

// Map technical posture severities to a simple, audit-friendly visual.
const SEV_BADGE = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
  informational: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const SeverityBadge = ({ severity, testId }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border capitalize ${SEV_BADGE[severity] || SEV_BADGE.low}`} data-testid={testId}>
    {severity}
  </span>
);
// Strip the "Google Admin Console → " prefix so fix locations stay terse.
const shortFix = (breadcrumb) => {
  if (!breadcrumb) return null;
  return breadcrumb.replace(/^Google Admin Console\s*→\s*/i, '').trim();
};

const GoogleWorkspaceDetail = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';

  const workspaceId = useMemo(
    () => JSON.parse(localStorage.getItem('current_workspace') || 'null')?.id,
    []
  );

  const [connection, setConnection] = useState(null);
  const [overview, setOverview] = useState(null);
  const [posture, setPosture] = useState(() => {
    // Seed from module cache so tab/back-navigation feels instant.
    const wsid = JSON.parse(localStorage.getItem('current_workspace') || 'null')?.id;
    return wsid ? (POSTURE_MEM_CACHE.get(wsid)?.posture || null) : null;
  });
  const [loadingConn, setLoadingConn] = useState(false);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingPosture, setLoadingPosture] = useState(false);
  // Background-evaluation tracking (re-evaluate button + first-time setup).
  const [evalJobId, setEvalJobId] = useState(null);
  const [evalStatus, setEvalStatus] = useState(null); // 'queued' | 'running' | 'succeeded' | 'failed'
  const evalPollRef = useRef(null);

  const loadConnection = useCallback(async () => {
    // Read user lazily so a slow auth-hydration race doesn't permanently
    // block the lookup. Workspace-scoped endpoint — any member sees it.
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    if (!u?.id || !workspaceId) {
      setConnection(null);
      return;
    }
    try {
      setLoadingConn(true);
      const resp = await axios.get(`/api/technologies/connections/${u.id}/${workspaceId}`);
      const conns = resp.data?.connections || [];
      const gw = conns.find(c => c.technology === 'google_workspace');
      setConnection(gw || null);
    } catch (err) {
      console.error('connection load failed', err);
    } finally {
      setLoadingConn(false);
    }
  }, [workspaceId]);

  const loadOverview = useCallback(async ({ silent = false } = {}) => {
    if (!workspaceId) return;
    // Hot-cache hit → render immediately. If stale, fall through to refetch
    // silently in the background (no spinner flash on tab switches).
    const cached = _readCache(OVERVIEW_MEM_CACHE, workspaceId);
    if (cached && !silent) {
      setOverview(cached.overview);
      if (!cached.isStale) return;
    }
    try {
      if (!silent && !cached) setLoadingOverview(true);
      const resp = await axios.get('/api/posture/google-workspace/overview',
        { params: { workspace_id: workspaceId } });
      setOverview(resp.data);
      _writeCache(OVERVIEW_MEM_CACHE, workspaceId, { overview: resp.data });
    } catch (err) {
      console.error('overview load failed', err);
      if (!silent) setOverview(null);
    } finally {
      setLoadingOverview(false);
    }
  }, [workspaceId]);

  const loadPosture = useCallback(async ({ silent = false } = {}) => {
    if (!workspaceId) return;
    // Hot-cache hit → render immediately while we silently refresh in the
    // background. The server itself returns from a snapshot, so the refresh
    // request is also fast.
    const cached = _readCache(POSTURE_MEM_CACHE, workspaceId);
    if (cached && !silent) {
      setPosture(cached.posture);
      if (!cached.isStale) return;
    }
    try {
      if (!silent && !cached) setLoadingPosture(true);
      const resp = await axios.get('/api/posture/google-workspace',
        { params: { workspace_id: workspaceId } });
      setPosture(resp.data);
      _writeCache(POSTURE_MEM_CACHE, workspaceId, { posture: resp.data });
    } catch (err) {
      console.error('posture load failed', err);
      if (!silent) setPosture(null);
    } finally {
      setLoadingPosture(false);
    }
  }, [workspaceId]);

  // Trigger a background re-evaluation. Returns once the job is queued so
  // the UI can flip into the "Evaluation running" state immediately. The
  // poller below will refetch the snapshot once the job completes.
  const triggerEvaluation = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const resp = await axios.post('/api/posture/google-workspace/evaluate', null,
        { params: { workspace_id: workspaceId } });
      const jobId = resp.data?.job_id;
      const deduped = !!resp.data?.deduped;
      if (!jobId) {
        toast.error('Could not start evaluation');
        return;
      }
      setEvalJobId(jobId);
      setEvalStatus(resp.data?.status || 'queued');
      if (deduped) {
        toast.info('Evaluation already in progress — tracking it now');
      } else {
        toast.success('Posture evaluation queued — this can take up to a minute');
      }
    } catch (err) {
      console.error('evaluate trigger failed', err);
      toast.error('Could not start evaluation');
    }
  }, [workspaceId]);

  // Poll the eval job. When it finishes, invalidate cache + refetch posture.
  useEffect(() => {
    if (!evalJobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`/api/posture/google-workspace/evaluate-jobs/${evalJobId}`);
        if (cancelled) return;
        const status = r.data?.status;
        setEvalStatus(status);
        if (status === 'succeeded') {
          invalidatePostureCache(workspaceId);
          await loadPosture({ silent: true });
          await loadOverview({ silent: true });
          setEvalJobId(null);
          toast.success('Posture re-evaluated');
        } else if (status === 'failed' || status === 'dead') {
          toast.error(`Evaluation failed: ${r.data?.error || 'unknown error'}`);
          setEvalJobId(null);
        }
      } catch (err) {
        if (!cancelled) console.warn('eval-job poll error', err?.message || err);
      }
    };
    // Poll every 3s — evaluator typically completes in <30s.
    evalPollRef.current = setInterval(tick, 3000);
    tick();
    return () => {
      cancelled = true;
      if (evalPollRef.current) clearInterval(evalPollRef.current);
    };
  }, [evalJobId, workspaceId, loadPosture, loadOverview]);

  useEffect(() => { loadConnection(); }, [loadConnection]);
  useEffect(() => {
    if (activeTab === 'overview') { loadOverview(); loadPosture(); }
    if (activeTab === 'posture') loadPosture();
  }, [activeTab, loadOverview, loadPosture]);

  const setTab = (id) => setSearchParams(prev => {
    const p = new URLSearchParams(prev);
    p.set('tab', id);
    return p;
  });

  const connStatus = connection?.status;
  const scopesPending = connection?.scopes_pending_reauth;

  return (
    <div className="min-h-screen bg-appbg">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="gw-detail-page">
        {/* Back Button — matches GitHub Evidence Details page */}
        <Button
          onClick={() => navigate('/technologies')}
          variant="outline"
          className="mb-6"
          data-testid="gw-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Technologies
        </Button>

        {/* Page title — same shape as GitHub Evidence Details */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Cloud className="w-6 h-6 text-emerald-700" strokeWidth={1.8} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Google Workspace</h1>
                <p className="text-slate-600">
                  Your workspace health, security checks and audit evidence — at a glance.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {connStatus === 'active' && (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 inline-flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Connected
                </Badge>
              )}
              {connStatus === 'pending_admin_approval' && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Pending admin approval
                </Badge>
              )}
              {scopesPending && (
                <Badge className="bg-blue-100 text-blue-800 border-blue-200 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Re-authorize needed
                </Badge>
              )}
            </div>
          </div>
        </div>

      <div className="border-b border-slate-200 mb-6 flex items-center gap-1 overflow-x-auto" data-testid="gw-tab-strip">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 transition-colors -mb-px whitespace-nowrap ${
                active
                  ? 'border-slate-900 text-slate-900 font-medium'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
              data-testid={`gw-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" strokeWidth={1.7} />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <OverviewTab
          connection={connection}
          overview={overview}
          posture={posture}
          workspaceId={workspaceId}
          loadingConn={loadingConn}
          loadingOverview={loadingOverview}
          onReload={() => { loadConnection(); loadOverview(); loadPosture(); }}
          onTab={setTab}
        />
      )}

      {activeTab === 'posture' && (
        <SecurityPostureTab
          posture={posture}
          loading={loadingPosture}
          workspaceId={workspaceId}
          scopesPending={scopesPending}
          onReload={loadPosture}
          onEvaluate={triggerEvaluation}
          evalStatus={evalStatus}
        />
      )}

      {activeTab === 'org-units' && (
        <OrgUnitsTab workspaceId={workspaceId} />
      )}

      {activeTab === 'evidence' && (
        <EvidenceLogsTab workspaceId={workspaceId} />
      )}

      {activeTab === 'settings' && (
        <SettingsTab workspaceId={workspaceId} scopesPending={scopesPending} />
      )}
      </div>
    </div>
  );
};


// =====================================================================
// Overview tab — connection health + stat cards + quick actions
// =====================================================================

const StatTile = ({ icon: Icon, label, value, sub, onClick, testId, tone = 'slate' }) => {
  const toneClasses = {
    slate:   'border-slate-200 hover:border-slate-300',
    red:     'border-red-200 hover:border-red-300 bg-red-50/30',
    amber:   'border-amber-200 hover:border-amber-300 bg-amber-50/30',
    emerald: 'border-emerald-200 hover:border-emerald-300 bg-emerald-50/30',
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`text-left bg-white border rounded-lg p-4 transition-colors w-full ${toneClasses} ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{label}</span>
        <Icon className="w-4 h-4 text-slate-400" strokeWidth={1.7} />
      </div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </button>
  );
};

const OverviewTab = ({ connection, overview, posture, workspaceId, loadingConn, loadingOverview, onReload, onTab }) => {
  // Loading: still waiting on the first data round-trip
  if ((loadingConn || loadingOverview) && !connection && !overview) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-10 text-center text-sm text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Loading workspace data…
        </CardContent>
      </Card>
    );
  }

  // True "disconnected" state: only when BOTH the connection lookup AND the
  // overview endpoint report no tenant data. This avoids the race where the
  // connection prop hasn't hydrated yet but the materialised data is right
  // there in `overview`.
  const tenantHasData = !!(overview && (
    (overview.counts?.users ?? 0) > 0 ||
    (overview.counts?.findings ?? 0) > 0 ||
    (overview.counts?.org_units ?? 0) > 0
  ));

  if (!connection && !tenantHasData) {
    return (
      <Card className="border-slate-200" data-testid="gw-overview-empty">
        <CardContent className="py-10 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
            <Cloud className="w-6 h-6 text-slate-400" strokeWidth={1.7} />
          </div>
          <p className="text-sm font-medium text-slate-800 mb-1">No Google Workspace connection yet</p>
          <p className="text-xs text-slate-500 mb-4 max-w-md mx-auto">
            Connect Google Workspace via Domain-Wide Delegation to unlock automated user, group, OU,
            and policy posture evaluation across 20+ deterministic rules.
          </p>
          <Button onClick={() => window.location.assign('/technologies')} data-testid="gw-overview-connect-btn">
            Go to Technology connections
          </Button>
          <ul className="text-[11px] text-slate-500 mt-5 max-w-sm mx-auto space-y-1 text-left">
            <li>• Live directory sync (users, groups, org units)</li>
            <li>• Org-wide 2SV / DLP / sharing posture (12 policy rules)</li>
            <li>• Automated audit-control evaluation + severity inheritance</li>
          </ul>
        </CardContent>
      </Card>
    );
  }

  const counts = overview?.counts || {};
  const sev = overview?.severity_counts || {};
  const lastSync = overview?.last_sync || {};
  const connected = (connection?.status === 'active') || tenantHasData;

  // Outcome-oriented rollup: total / passed / failed + how-severe.
  const totalChecks = posture?.findings_count ?? counts.findings ?? 0;
  const failed = (sev.critical || 0) + (sev.high || 0) + (sev.medium || 0);
  const passed = Math.max(0, totalChecks - failed);
  const score = posture?.score != null ? Math.round(posture.score) : null;
  const postureLabel = score == null ? '—' : score >= 85 ? 'Healthy' : score >= 60 ? 'Needs Attention' : 'At Risk';
  const postureTone = score == null ? 'text-slate-500' : score >= 85 ? 'text-emerald-600' : score >= 60 ? 'text-amber-600' : 'text-red-600';
  const lastSyncedAt = lastSync.users || lastSync.org_units || lastSync.groups;
  const priorityActions = (posture?.top_fixes || []).filter(
    (f) => f.severity === 'critical' || f.severity === 'high' || f.severity === 'medium'
  );

  const HealthLine = ({ label, value, ok, testId }) => (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-b-0" data-testid={testId}>
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900 inline-flex items-center gap-1.5">
        {ok && <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />}
        {value}
      </span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="gw-overview">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Workspace Health */}
        <Card className="border-slate-200" data-testid="gw-overview-health">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700">Workspace Health</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <HealthLine label="Connection" value={connected ? 'Connected' : 'Not connected'} ok={connected} testId="gw-health-connection" />
            <HealthLine label="Users" value={`${counts.users ?? 0} synced`} testId="gw-health-users" />
            <HealthLine label="Groups" value={`${counts.groups ?? 0} synced`} testId="gw-health-groups" />
            <HealthLine label="Departments" value={`${counts.org_units ?? 0} synced`} testId="gw-health-departments" />
            <HealthLine label="Last synced" value={lastSyncedAt ? formatDate(lastSyncedAt) : '—'} testId="gw-health-last-synced" />
          </CardContent>
        </Card>

        {/* Security Checks */}
        <Card className="border-slate-200" data-testid="gw-overview-checks">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700">Security Checks</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="text-3xl font-bold text-slate-900 mb-3" data-testid="gw-checks-total">{totalChecks} checks</div>
            <div className="flex items-center gap-4 mb-3">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700" data-testid="gw-checks-passed">
                <CheckCircle className="w-4 h-4" /> Passed {passed}
              </span>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-red-700" data-testid="gw-checks-failed">
                <X className="w-4 h-4" /> Failed {failed}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <SeverityBadge severity="critical" /> <span className="text-xs text-slate-500 self-center">{sev.critical || 0}</span>
              <SeverityBadge severity="high" /> <span className="text-xs text-slate-500 self-center">{sev.high || 0}</span>
              <SeverityBadge severity="medium" /> <span className="text-xs text-slate-500 self-center">{sev.medium || 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Security Posture */}
        <Card className="border-slate-200" data-testid="gw-overview-posture">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700">Security Posture</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className={`text-5xl font-bold ${postureTone}`} data-testid="gw-posture-score">{score == null ? '—' : `${score}%`}</div>
            <div className={`mt-2 text-sm font-medium ${postureTone}`} data-testid="gw-posture-label">{postureLabel}</div>
          </CardContent>
        </Card>
      </div>

      {/* Priority Actions */}
      <Card className="border-slate-200" data-testid="gw-overview-priority-actions">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-slate-500" /> Priority Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {priorityActions.length === 0 ? (
            <p className="text-sm text-slate-500 py-2 inline-flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" /> No critical, high or medium issues. You're audit-ready here.
            </p>
          ) : (
            <div className="divide-y divide-slate-100">
              {priorityActions.map((f, i) => {
                const fix = shortFix(f.fix_breadcrumb || f.how_to_fix?.breadcrumb);
                return (
                  <div key={f.rule_key} className="py-3 flex items-start justify-between gap-4" data-testid={`gw-priority-action-${i}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <SeverityBadge severity={f.severity} testId={`gw-priority-sev-${i}`} />
                        <span className="text-sm font-semibold text-slate-900">{f.label}</span>
                      </div>
                      {f.frameworks?.length > 0 && (
                        <div className="text-xs text-slate-500 mb-1">{f.frameworks.join(' • ')}</div>
                      )}
                      {fix && <div className="text-xs text-slate-600"><span className="text-slate-400">Fix:</span> {fix}</div>}
                    </div>
                    {f.score_impact > 0 && (
                      <span className="text-sm font-semibold text-emerald-700 shrink-0" data-testid={`gw-priority-points-${i}`}>+{f.score_impact} points</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2" data-testid="gw-overview-quick-actions">
        <Button variant="outline" onClick={() => onTab('posture')} data-testid="gw-overview-posture-btn">
          <Shield className="w-4 h-4 mr-2" /> View Security Checks
        </Button>
        <Button variant="outline" onClick={() => onTab('evidence')} data-testid="gw-overview-evidence-btn">
          <FileText className="w-4 h-4 mr-2" /> View Audit Evidence
        </Button>
        <Button variant="outline" onClick={onReload} data-testid="gw-overview-reload-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>
    </div>
  );
};

// ----- Command Center widgets -----

const GRADE_TONE = (g) =>
  g === 'A' ? 'text-emerald-700 ring-emerald-300'
  : g === 'B' ? 'text-emerald-600 ring-emerald-300'
  : g === 'C' ? 'text-amber-600 ring-amber-300'
  : g === 'D' ? 'text-orange-600 ring-orange-300'
  : 'text-red-600 ring-red-300';

const PostureSummaryCard = ({ posture, workspaceId, onOpenPosture }) => {
  const [explain, setExplain] = useState(null);
  const [open, setOpen] = useState(false);

  const openExplain = async () => {
    if (!workspaceId) return;
    setOpen(true);
    if (explain) return;
    try {
      const r = await axios.get('/api/posture/google-workspace/score-explain',
        { params: { workspace_id: workspaceId } });
      setExplain(r.data);
    } catch (e) {
      toast.error('Could not load score breakdown');
    }
  };

  if (!posture) {
    return (
      <Card className="border-slate-200" data-testid="gw-overview-posture-summary-empty">
        <CardContent className="py-6 text-sm text-slate-500 text-center">
          Loading posture…
        </CardContent>
      </Card>
    );
  }

  // First-time / no snapshot yet — keep the Overview card useful instead
  // of showing meaningless score/grade values.
  if (posture.cache_status === 'missing' || posture.needs_evaluation) {
    return (
      <Card className="border-slate-200" data-testid="gw-overview-posture-summary-needs-eval">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
            <Shield className="w-4 h-4 text-slate-500" />
            Posture summary
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-xs text-slate-600 mb-3">
            No posture evaluation yet. Run one from the Security Posture
            tab to see your score, top fixes, and per-domain findings.
          </p>
          <Button size="sm" variant="outline" className="w-full"
                  onClick={onOpenPosture}
                  data-testid="gw-overview-posture-needs-eval-btn">
            Open Security Posture <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const sc = posture.severity_counts || {};
  const tone = GRADE_TONE(posture.grade);

  return (
    <Card className="border-slate-200" data-testid="gw-overview-posture-summary">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
          <Shield className="w-4 h-4 text-slate-500" />
          Posture summary
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center gap-3 mb-3">
          <div className={`inline-flex flex-col items-center justify-center w-14 h-14 rounded-full bg-white ring-4 ${tone}`}
               data-testid="gw-overview-score">
            <div className="text-lg font-semibold leading-none">{Math.round(posture.score || 0)}</div>
            <div className="text-[9px] uppercase tracking-wider mt-0.5 font-medium">{posture.grade || '—'}</div>
          </div>
          <div className="min-w-0">
            <button
              type="button"
              onClick={openExplain}
              className="text-xs text-slate-700 underline-offset-2 hover:underline"
              data-testid="gw-overview-score-explain-btn"
            >
              Why this score?
            </button>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {posture.findings_count || 0} rules evaluated · {posture.alert_eligible_count || 0} alert-eligible
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {['critical', 'high', 'medium', 'low', 'informational'].map(s => (
            <span key={s}
                  className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-700"
                  data-testid={`gw-overview-sev-${s}`}>
              <span className={`inline-block w-2 h-2 rounded-full ${
                s === 'critical' ? 'bg-red-600 ring-1 ring-red-200' :
                s === 'high' ? 'bg-orange-500' :
                s === 'medium' ? 'bg-amber-500' :
                s === 'low' ? 'bg-slate-400' : 'bg-emerald-500'
              }`} />
              {sc[s] || 0} {s}
            </span>
          ))}
        </div>
        <Button onClick={onOpenPosture} variant="outline" size="sm" className="w-full mt-3"
                data-testid="gw-overview-posture-open-btn">
          Open Security Posture
        </Button>

        {open && (
          <ScoreExplainModal
            explain={explain}
            onClose={() => setOpen(false)}
          />
        )}
      </CardContent>
    </Card>
  );
};

const ScoreExplainModal = ({ explain, onClose }) => {
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
         data-testid="gw-score-explain-modal" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Why is my score {explain ? Math.round(explain.score || 0) : '—'}?</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Posture starts at 100 and is reduced by every failing rule. Hard caps clamp the score regardless of arithmetic.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 text-xl leading-none" data-testid="gw-score-explain-close">×</button>
        </div>
        <div className="p-5 space-y-3">
          {!explain && (
            <div className="text-center text-sm text-slate-500 py-6">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Loading deductions…
            </div>
          )}
          {explain?.caps_active?.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-medium text-red-800 mb-1">Active hard caps</p>
              <ul className="text-xs text-red-900 list-disc ml-4 space-y-0.5">
                {explain.caps_active.map(c => (
                  <li key={c.rule_key}>{c.label} — score capped at {c.max_score}</li>
                ))}
              </ul>
            </div>
          )}
          {explain?.deductions?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-700 mb-2 uppercase tracking-wide">Deductions</p>
              <div className="divide-y divide-slate-100 border border-slate-200 rounded">
                {explain.deductions.map(d => (
                  <div key={d.rule_key} className="px-3 py-2 flex items-start justify-between gap-3 text-xs"
                       data-testid={`gw-score-explain-row-${d.rule_key}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900">{d.label}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        <span className="inline-block px-1.5 py-0 rounded border border-slate-200 mr-1">{d.severity}</span>
                        weight {d.severity_weight} × cardinality {d.cardinality_multiplier} ({d.match_count} match{d.match_count !== 1 ? 'es' : ''})
                        · effort {d.effort}
                      </div>
                      {d.what_would_fix && (
                        <div className="text-[11px] text-slate-600 mt-1">Fix: {d.what_would_fix}</div>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-red-700 shrink-0">−{d.deduction}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {explain && (
            <p className="text-[11px] text-slate-500 mt-2">
              Score arithmetic: 100 − Σ(deductions) = {explain.raw_score}.
              {explain.caps_active?.length > 0 && ` Capped at ${explain.score}.`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const SyncHealthCard = ({ overview, connection }) => {
  const last = overview?.last_sync || {};
  const verifSuccess = connection?.last_verification?.success;
  const probeAt = connection?.last_verification?.at;
  const scopesPending = connection?.scopes_pending_reauth;

  const fresh = (iso) => {
    if (!iso) return { tone: 'text-slate-500', label: 'never' };
    const ageH = (Date.now() - new Date(iso).getTime()) / 3.6e6;
    if (ageH < 24) return { tone: 'text-emerald-700', label: 'today' };
    if (ageH < 24 * 7) return { tone: 'text-amber-700', label: `${Math.round(ageH / 24)}d ago` };
    return { tone: 'text-red-700', label: `${Math.round(ageH / 24)}d ago — stale` };
  };

  const items = [
    { label: 'Users', at: last.users },
    { label: 'Groups', at: last.groups },
    { label: 'Org units', at: last.org_units },
    { label: 'Posture eval', at: last.posture_evaluated_at },
  ];

  return (
    <Card className="border-slate-200" data-testid="gw-overview-sync-health">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
          <Activity className="w-4 h-4 text-slate-500" />
          Sync health
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-1.5">
        <div className="text-xs flex items-center justify-between border-b border-slate-100 pb-1.5 mb-1.5">
          <span className="text-slate-500">DWD probe</span>
          <span className={`font-medium ${verifSuccess ? 'text-emerald-700' : 'text-red-700'}`}
                data-testid="gw-overview-dwd-status">
            {verifSuccess === true ? `OK · ${probeAt ? formatDate(probeAt) : '—'}` :
             verifSuccess === false ? 'Failed' : 'No probe'}
          </span>
        </div>
        {items.map(it => {
          const f = fresh(it.at);
          return (
            <div key={it.label} className="text-xs flex items-center justify-between"
                 data-testid={`gw-overview-sync-${it.label.toLowerCase().replace(/\s/g,'-')}`}>
              <span className="text-slate-500">{it.label}</span>
              <span className={`font-medium ${f.tone}`}>{f.label}</span>
            </div>
          );
        })}
        {scopesPending && (
          <div className="mt-2 text-[11px] text-blue-800 bg-blue-50 border border-blue-200 rounded p-2">
            New scopes available — re-authorize DWD to unlock 12 policy rules.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TopFindingsCard = ({ posture, workspaceId, onOpenPosture }) => {
  const top = (posture?.top_fixes || []).slice(0, 4);
  return (
    <Card className="border-slate-200" data-testid="gw-overview-top-findings">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
          <ShieldAlert className="w-4 h-4 text-slate-500" />
          Top findings to fix
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {top.length === 0 && (
          <p className="text-xs text-slate-500 py-2">No active findings — your workspace is clean. 🎉</p>
        )}
        <div className="space-y-2">
          {top.map((f, i) => (
            <div key={f.rule_key}
                 className={`text-xs border rounded-md px-2.5 py-2 ${
                   f.severity === 'critical' ? 'border-l-4 border-l-red-600 border-red-200 bg-red-50/40' :
                   f.severity === 'high' ? 'border-l-4 border-l-orange-500 border-orange-200' :
                   'border-slate-200'
                 }`}
                 data-testid={`gw-overview-top-finding-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-900 truncate">{f.label || f.rule_key}</span>
                <span className="text-emerald-700 font-semibold shrink-0">+{f.score_impact}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{f.what_would_fix}</div>
            </div>
          ))}
        </div>
        {top.length > 0 && (
          <Button onClick={onOpenPosture} variant="ghost" size="sm" className="w-full mt-2 text-xs"
                  data-testid="gw-overview-top-findings-open">
            View all top fixes →
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const ActivityFeedCard = ({ workspaceId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let active = true;
    setLoading(true);
    axios.get('/api/posture/activity', { params: { workspace_id: workspaceId, limit: 12 } })
      .then(r => { if (active) setEvents(r.data?.events || []); })
      .catch(() => { if (active) setEvents([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [workspaceId]);

  const dotFor = (sev, kind) => {
    if (kind === 'evidence_collected') return 'bg-blue-500';
    if (kind === 'finding_acknowledged') return 'bg-slate-400';
    return {
      critical: 'bg-red-600 ring-2 ring-red-200',
      high: 'bg-orange-500',
      medium: 'bg-amber-500',
      low: 'bg-slate-400',
      informational: 'bg-emerald-500',
    }[sev] || 'bg-slate-400';
  };

  return (
    <Card className="border-slate-200" data-testid="gw-overview-activity-feed">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
          <Activity className="w-4 h-4 text-slate-500" />
          Recent activity
        </CardTitle>
        <CardDescription className="text-[11px]">
          The last few posture and evidence events in this workspace.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && events.length === 0 && (
          <div className="text-xs text-slate-500 py-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> Loading…
          </div>
        )}
        {!loading && events.length === 0 && (
          <div className="text-xs text-slate-500 py-2">No recent activity yet.</div>
        )}
        {events.length > 0 && (
          <ol className="relative ml-2 border-l border-slate-200 space-y-3 pl-4 py-1">
            {events.map((e, i) => (
              <li key={i} className="relative" data-testid={`gw-activity-event-${i}`}>
                <span className={`absolute -left-[1.4rem] top-1 inline-block w-2.5 h-2.5 rounded-full ${dotFor(e.severity, e.kind)}`} />
                <div className="text-xs">
                  <div className="font-medium text-slate-900">{e.title}</div>
                  {e.detail && <div className="text-slate-500 mt-0.5 line-clamp-2">{e.detail}</div>}
                  <div className="text-[10px] text-slate-400 mt-1">{e.at ? formatDate(e.at) : ''}</div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};

const Row = ({ label, value, testId }) => (
  <div className="flex items-center justify-between border-b border-slate-100 py-1.5 last:border-b-0">
    <span className="text-slate-500 text-xs uppercase tracking-wide">{label}</span>
    <span className="font-mono text-xs text-slate-900 truncate max-w-[60%]" data-testid={testId}>{value}</span>
  </div>
);


// =====================================================================
// Security Posture tab
// =====================================================================

const SecurityPostureTab = ({ posture, loading, workspaceId, scopesPending, onReload, onEvaluate, evalStatus }) => {
  // First-ever load (no cache, no fetch result yet): a polite shimmer.
  if (loading && !posture) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-10 text-center text-sm text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Loading posture…
        </CardContent>
      </Card>
    );
  }

  // No snapshot yet (first-time setup, or evidence not yet evaluated).
  // We don't want users to see a blank or half-rendered page in
  // production — show a clear "Run evaluation" call to action instead.
  const isMissing = posture && (posture.cache_status === 'missing' || posture.needs_evaluation);
  const isEvaluating = evalStatus === 'queued' || evalStatus === 'running';

  return (
    <div className="space-y-6" data-testid="gw-posture-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900">Security Checks</h2>
        <PostureFreshnessPill
          posture={posture}
          isEvaluating={isEvaluating}
          onEvaluate={onEvaluate}
        />
      </div>

      {scopesPending && <ScopePendingNotice />}

      {isMissing ? (
        <PostureEmptyState onEvaluate={onEvaluate} isEvaluating={isEvaluating} />
      ) : (
        posture && <SecurityChecksTable posture={posture} />
      )}
    </div>
  );
};


// "Last evaluated · Re-evaluate" pill anchored to the Posture page hero.
// Communicates server-cache freshness + gives users a one-click manual
// refresh that dispatches a background job (no page-blocking spinner).
export const PostureFreshnessPill = ({ posture, isEvaluating, onEvaluate, testIdPrefix = 'gw' }) => {
  const evaluatedAt = posture?.evaluated_at;
  const ageSeconds = posture?.cache_age_seconds;
  const fromCache = posture?.from_cache;

  let ageLabel = '—';
  if (evaluatedAt) {
    if (ageSeconds == null) {
      ageLabel = formatDate(evaluatedAt);
    } else if (ageSeconds < 60) {
      ageLabel = 'just now';
    } else if (ageSeconds < 3600) {
      ageLabel = `${Math.floor(ageSeconds / 60)} min ago`;
    } else if (ageSeconds < 86400) {
      ageLabel = `${Math.floor(ageSeconds / 3600)} hr ago`;
    } else {
      ageLabel = formatDate(evaluatedAt);
    }
  }

  const isStale = fromCache && ageSeconds != null && ageSeconds > 15 * 60;

  return (
    <div className="inline-flex items-center gap-2"
         data-testid={`${testIdPrefix}-posture-freshness-pill`}>
      {isEvaluating ? (
        <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border border-blue-200 bg-blue-50 text-blue-700"
              data-testid={`${testIdPrefix}-posture-evaluating-pill`}>
          <Loader2 className="w-3 h-3 animate-spin" />
          Evaluation running…
        </span>
      ) : evaluatedAt ? (
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border ${
            isStale
              ? 'border-amber-200 bg-amber-50 text-amber-800'
              : 'border-slate-200 bg-slate-50 text-slate-700'
          }`}
          title={`Evaluated at ${evaluatedAt}`}
          data-testid={`${testIdPrefix}-posture-evaluated-at-pill`}
        >
          <Clock className="w-3 h-3" />
          Last evaluated · {ageLabel}
        </span>
      ) : null}
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-7 text-xs"
        onClick={onEvaluate}
        disabled={isEvaluating}
        data-testid={`${testIdPrefix}-posture-reevaluate-btn`}
      >
        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${isEvaluating ? 'animate-spin' : ''}`} />
        Re-evaluate
      </Button>
    </div>
  );
};

// First-time empty state — never let production users see a blank page.
// Pairs a clear explanation with the same Re-evaluate button so the
// experience stays consistent with the freshness pill above.
const PostureEmptyState = ({ onEvaluate, isEvaluating }) => (
  <Card className="border-slate-200" data-testid="gw-posture-empty-state">
    <CardContent className="py-10 text-center">
      <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
        <Shield className="w-6 h-6 text-slate-500" strokeWidth={1.7} />
      </div>
      <h3 className="text-base font-semibold text-slate-900 mb-1">
        Posture evaluation hasn't run yet
      </h3>
      <p className="text-sm text-slate-600 max-w-md mx-auto mb-4">
        Run your first Google Workspace posture evaluation to surface Top
        Fixes, severity-ranked findings, and per-domain rules. Evaluations
        run in the background and typically complete in under a minute.
      </p>
      <Button
        type="button"
        onClick={onEvaluate}
        disabled={isEvaluating}
        className="bg-slate-900 hover:bg-slate-800 text-white"
        data-testid="gw-posture-empty-evaluate-btn"
      >
        {isEvaluating ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Evaluation running…</>
        ) : (
          <><Play className="w-4 h-4 mr-2" />Run evaluation now</>
        )}
      </Button>
    </CardContent>
  </Card>
);

const ScopePendingNotice = () => (
  <div className="rounded-md border border-blue-200 bg-blue-50 p-3 mb-4 text-sm">
    <p className="font-medium text-blue-900 mb-1">Re-authorize Nova to unlock posture checks</p>
    <p className="text-xs text-blue-800 mb-2">
      Your Google Workspace connection is on <span className="font-mono">scopes_v1</span>.
      Adding the <span className="font-mono">cloud-identity.policies.readonly</span> scope unlocks
      password / sharing / Gmail / DLP posture (12 rules) with zero additional permissions on user data.
    </p>
    <Button
      type="button"
      size="sm"
      className="bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1"
      onClick={() => window.open('https://admin.google.com/ac/owl/domainwidedelegation', '_blank', 'noopener,noreferrer')}
    >
      <ExternalLink className="w-3.5 h-3.5" />
      Open Google Admin Console
    </Button>
  </div>
);


// =====================================================================
// Organizational Units tab — searchable list + sync action
// =====================================================================

const OrgUnitsTab = ({ workspaceId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [riskyOnly, setRiskyOnly] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set(['/'])); // root open by default
  const [selected, setSelected] = useState(null);  // selected OU path → side panel

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      try {
        const r = await axios.get('/api/posture/google-workspace/by-ou',
          { params: { workspace_id: workspaceId } });
        setData(r.data);
      } catch (e) {
        const r2 = await axios.get('/api/employees/org-units',
          { params: { workspace_id: workspaceId } });
        setData(r2.data);
      }
    } catch (e) {
      console.error('org-units load failed', e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const triggerSync = async () => {
    if (!workspaceId) return;
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (!user?.id) return toast.error('Sign in required');
    try {
      setSyncing(true);
      const r = await axios.post('/api/technologies/collect-evidence/google-workspace', {
        user_id: user.id,
        workspace_id: workspaceId,
      });
      toast.success(r.data?.message || 'Sync started');
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const allOUs = data?.org_units || [];
  const totals = data?.totals || {};

  // Build path → OU lookup and parent → children adjacency
  const { byPath, childrenOf, roots } = useMemo(() => {
    const byPath = {};
    for (const ou of allOUs) {
      const p = ou.org_unit_path || '/';
      byPath[p] = ou;
    }
    const childrenOf = {};
    const roots = [];
    for (const ou of allOUs) {
      const p = ou.org_unit_path || '/';
      // Derive parent from path: '/Eng/Backend' → '/Eng'; '/Eng' → '/'; '/' → null
      let parent;
      if (p === '/' || p === '') {
        parent = null;
      } else {
        const segs = p.split('/').filter(Boolean);
        parent = segs.length <= 1 ? '/' : '/' + segs.slice(0, -1).join('/');
      }
      if (parent && byPath[parent]) {
        (childrenOf[parent] ||= []).push(p);
      } else {
        roots.push(p);
      }
    }
    // Sort siblings by name for stable tree
    for (const k of Object.keys(childrenOf)) {
      childrenOf[k].sort((a, b) => (byPath[a].name || '').localeCompare(byPath[b].name || ''));
    }
    return { byPath, childrenOf, roots: [...new Set(roots)].sort() };
  }, [allOUs]);

  // Build a "matches the current search/filter" set — used to keep ancestors visible
  const visiblePaths = useMemo(() => {
    if (!data) return new Set();
    const q = search.trim().toLowerCase();
    const matched = new Set();
    for (const ou of allOUs) {
      const p = ou.org_unit_path || '/';
      if (riskyOnly && !ou.risky) continue;
      if (q && !(ou.name || '').toLowerCase().includes(q) && !p.toLowerCase().includes(q)) continue;
      matched.add(p);
    }
    // Add every ancestor so the tree stays navigable
    const out = new Set(matched);
    for (const p of matched) {
      let cur = p;
      while (cur && cur !== '/' ) {
        const segs = cur.split('/').filter(Boolean);
        cur = segs.length <= 1 ? '/' : '/' + segs.slice(0, -1).join('/');
        out.add(cur);
      }
    }
    return out;
  }, [allOUs, search, riskyOnly, data]);

  const toggle = (p) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  };
  const expandAll = () => setExpanded(new Set(Object.keys(byPath)));
  // Collapse every branch — including root — so only the root row is visible.
  // The root row stays clickable to re-open the tree.
  const collapseAll = () => setExpanded(new Set());

  const selectOU = (path) => setSelected(path);
  const closeSelected = () => setSelected(null);

  return (
    <Card className="border-slate-200" data-testid="gw-ou-tab">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-slate-500" strokeWidth={1.7} />
              Teams &amp; Departments
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={triggerSync} variant="outline" size="sm" disabled={syncing}
                    data-testid="gw-ou-sync-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              Sync now
            </Button>
            <Button onClick={load} variant="ghost" size="sm" disabled={loading}
                    data-testid="gw-ou-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {loading && !data && (
          <div className="py-6 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading OU structure…
          </div>
        )}

        {data && allOUs.length === 0 && (
          <div className="py-6 text-center text-sm text-slate-500" data-testid="gw-ou-empty">
            <Folder className="w-7 h-7 mx-auto text-slate-300 mb-2" />
            No organizational units synced yet. Click <span className="font-medium">Sync now</span> to fetch them from Google Workspace.
          </div>
        )}

        {data && allOUs.length > 0 && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-md border border-slate-200 bg-white px-3 py-2"
                   data-testid="gw-ou-summary-ous">
                <div className="text-[11px] text-slate-500 uppercase tracking-wide">Departments</div>
                <div className="text-lg font-semibold text-slate-900">{totals.org_units_total ?? allOUs.length}</div>
              </div>
              <div className="rounded-md border border-slate-200 bg-white px-3 py-2"
                   data-testid="gw-ou-summary-users">
                <div className="text-[11px] text-slate-500 uppercase tracking-wide">Users</div>
                <div className="text-lg font-semibold text-slate-900">{totals.users_total ?? 0}</div>
              </div>
              <div className={`rounded-md border px-3 py-2 ${(totals.risky_ou_count || 0) > 0 ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-white'}`}
                   data-testid="gw-ou-summary-risky">
                <div className="text-[11px] uppercase tracking-wide flex items-center gap-1"
                     style={{ color: (totals.risky_ou_count || 0) > 0 ? '#b91c1c' : '#64748b' }}>
                  <ShieldAlert className="w-3 h-3" />
                  Risky Departments
                </div>
                <div className={`text-lg font-semibold ${(totals.risky_ou_count || 0) > 0 ? 'text-red-700' : 'text-slate-900'}`}>
                  {totals.risky_ou_count ?? 0}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setRiskyOnly(v => !v)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    riskyOnly
                      ? 'border-red-600 bg-red-600 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                  }`}
                  data-testid="gw-ou-risky-filter"
                >
                  Risky only
                </button>
                <button
                  type="button"
                  onClick={expandAll}
                  className="text-xs px-2 py-1 rounded border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  data-testid="gw-ou-expand-all"
                >
                  Expand all
                </button>
                <button
                  type="button"
                  onClick={collapseAll}
                  className="text-xs px-2 py-1 rounded border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  data-testid="gw-ou-collapse-all"
                >
                  Collapse
                </button>
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter by name or path"
                  className="pl-8 h-8 text-sm"
                  data-testid="gw-ou-search"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-3">
              <div className="border rounded-lg border-slate-200 bg-white">
                <div className="px-3 py-2 border-b border-slate-100 text-xs font-medium text-slate-700 flex justify-between">
                  <span>OU tree</span>
                  <span>{visiblePaths.size} of {allOUs.length} shown</span>
                </div>
                <div className="divide-y divide-slate-100 max-h-[560px] overflow-y-auto"
                     data-testid="gw-ou-tree">
                  {visiblePaths.size === 0 && (
                    <div className="px-3 py-6 text-center text-xs text-slate-500">
                      {riskyOnly ? 'No risky OUs in this workspace 🎉' : `No OUs match "${search}"`}
                    </div>
                  )}
                  {roots.map(rootPath => (
                    <OuTreeNode
                      key={rootPath}
                      path={rootPath}
                      byPath={byPath}
                      childrenOf={childrenOf}
                      visiblePaths={visiblePaths}
                      expanded={expanded}
                      onToggle={toggle}
                      depthLevel={0}
                      onSelect={selectOU}
                      selectedPath={selected}
                    />
                  ))}
                </div>
              </div>

              <SelectedOuPanel
                ou={selected ? byPath[selected] : null}
                byPath={byPath}
                childrenOf={childrenOf}
                onClose={closeSelected}
                onSelect={selectOU}
              />
            </div>

            <div className="mt-3 text-[11px] text-slate-400">
              Evaluated {formatDate(data.evaluated_at)}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ── OU tree node (recursive) ─────────────────────────────────────────────

const SEV_DOT_CLASSES = {
  critical: 'bg-red-600 ring-2 ring-red-200',
  high: 'bg-orange-500',
  medium: 'bg-amber-500',
  low: 'bg-slate-400',
  informational: 'bg-emerald-500',
};

// Subtle heatmap — the larger the critical+high count, the deeper the tint.
const heatRowTint = (sc) => {
  const k = (sc?.critical || 0) * 2 + (sc?.high || 0);
  if (k >= 6) return 'bg-red-100/70';
  if (k >= 3) return 'bg-red-50/60';
  if (k >= 1) return 'bg-red-50/30';
  return '';
};

const OuTreeNode = ({ path, byPath, childrenOf, visiblePaths, expanded, onToggle, depthLevel, onSelect, selectedPath }) => {
  const ou = byPath[path];
  if (!ou) return null;
  if (!visiblePaths.has(path)) return null;

  const kids = childrenOf[path] || [];
  const hasKids = kids.length > 0;
  const isOpen = expanded.has(path);
  const sc = ou.severity_counts || {};
  const risky = ou.risky;
  const isSelected = selectedPath === path;
  const heat = heatRowTint(sc);

  return (
    <>
      <div
        className={`flex items-center justify-between gap-2 text-sm transition-colors cursor-pointer ${
          isSelected ? 'bg-slate-100' : risky ? heat || 'bg-red-50/30' : ''
        } hover:bg-slate-50`}
        style={{ paddingLeft: `${0.5 + depthLevel * 1.1}rem`, paddingRight: '0.75rem', paddingTop: '0.4rem', paddingBottom: '0.4rem' }}
        onClick={() => onSelect(path)}
        data-testid={`gw-ou-row-${ou.source_org_unit_id}`}
        title="Click to view findings affecting this OU"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); if (hasKids) onToggle(path); }}
            className={`w-4 h-4 inline-flex items-center justify-center text-slate-500 ${hasKids ? 'hover:text-slate-900' : 'opacity-0 pointer-events-none'}`}
            data-testid={`gw-ou-toggle-${ou.source_org_unit_id}`}
            title={isOpen ? 'Collapse' : 'Expand'}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            {hasKids ? (isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />) : null}
          </button>
          <Folder className={`w-3.5 h-3.5 shrink-0 ${risky ? 'text-red-500' : 'text-slate-400'}`} />
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate flex items-center gap-1.5">
              {ou.name || '(unnamed)'}
              <InheritanceIndicator ou={ou} />
              {risky && (
                <span className="text-[10px] px-1.5 py-0 rounded border border-red-300 bg-red-100 text-red-700 inline-flex items-center gap-0.5"
                      title="Has at least one Critical or High posture finding">
                  <ShieldAlert className="w-2.5 h-2.5" />
                  risky
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 truncate">{ou.org_unit_path}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-1.5">
            {['critical', 'high', 'medium'].map(s => sc[s] ? (
              <span key={s}
                    className="inline-flex items-center gap-1 text-[10px] text-slate-700"
                    data-testid={`gw-ou-sev-${ou.source_org_unit_id}-${s}`}
                    title={`${sc[s]} ${s} finding${sc[s] !== 1 ? 's' : ''} affecting users in this OU. Click row to drill in.`}>
                <span className={`inline-block w-2 h-2 rounded-full ${SEV_DOT_CLASSES[s]}`} />
                {sc[s]}
              </span>
            ) : null)}
          </div>
          <div className="text-xs text-slate-500">
            {ou.user_count} user{ou.user_count !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
      {hasKids && isOpen && kids.map(c => (
        <OuTreeNode
          key={c}
          path={c}
          byPath={byPath}
          childrenOf={childrenOf}
          visiblePaths={visiblePaths}
          expanded={expanded}
          onToggle={onToggle}
          depthLevel={depthLevel + 1}
          onSelect={onSelect}
          selectedPath={selectedPath}
        />
      ))}
    </>
  );
};

// Inheritance indicator: locked = parent rules don't apply; linked = inherited
const InheritanceIndicator = ({ ou }) => {
  if (ou.org_unit_path === '/' || !ou.org_unit_path) {
    return (
      <span title="Top-level OU — defines defaults for the whole tenant"
            className="inline-flex items-center text-[10px] text-slate-500"
            data-testid="gw-ou-inheritance-root">
        <Link2 className="w-2.5 h-2.5 mr-0.5" />
        root
      </span>
    );
  }
  if (ou.block_inheritance) {
    return (
      <span title="Inheritance blocked — parent settings do not propagate here"
            className="inline-flex items-center text-[10px] px-1.5 py-0 rounded border border-amber-200 bg-amber-50 text-amber-700"
            data-testid="gw-ou-inheritance-blocked">
        <Lock className="w-2.5 h-2.5 mr-0.5" />
        inheritance blocked
      </span>
    );
  }
  return (
    <span title="Inherits parent OU settings"
          className="inline-flex items-center text-[10px] text-emerald-700"
          data-testid="gw-ou-inheritance-inherits">
      <Unlock className="w-2.5 h-2.5 mr-0.5" />
      inherits
    </span>
  );
};

// Side panel: findings affecting the selected OU + breadcrumbs
const SelectedOuPanel = ({ ou, byPath, childrenOf, onClose, onSelect }) => {
  if (!ou) {
    return (
      <div className="hidden lg:flex border border-slate-200 rounded-lg bg-slate-50/40 p-4 items-center justify-center text-center text-xs text-slate-500"
           data-testid="gw-ou-panel-empty">
        Click an OU to see findings + breadcrumbs.
      </div>
    );
  }

  // Breadcrumbs — every segment in the path
  const segs = (ou.org_unit_path || '/').split('/').filter(Boolean);
  const crumbs = [{ name: 'Root', path: '/' }];
  let acc = '';
  for (const s of segs) {
    acc = `${acc}/${s}`;
    crumbs.push({ name: s, path: acc });
  }

  const findings = ou.findings || [];
  const sc = ou.severity_counts || {};
  const kids = (childrenOf[ou.org_unit_path] || []).map(p => byPath[p]).filter(Boolean);

  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid="gw-ou-panel">
      <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center flex-wrap gap-0.5"
               data-testid="gw-ou-breadcrumbs">
            {crumbs.map((c, i) => (
              <span key={c.path} className="inline-flex items-center">
                <button
                  type="button"
                  onClick={() => onSelect(c.path)}
                  className={`hover:text-slate-900 ${c.path === ou.org_unit_path ? 'text-slate-700 font-medium' : ''}`}
                  data-testid={`gw-ou-crumb-${i}`}
                >
                  {c.name}
                </button>
                {i < crumbs.length - 1 && <ChevronRight className="w-3 h-3 mx-0.5 text-slate-300" />}
              </span>
            ))}
          </div>
          <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
            {ou.name}
            {ou.risky && (
              <span className="text-[10px] px-1.5 py-0 rounded border border-red-300 bg-red-100 text-red-700 inline-flex items-center gap-0.5">
                <ShieldAlert className="w-2.5 h-2.5" />
                risky
              </span>
            )}
          </div>
        </div>
        <button type="button" onClick={onClose}
                className="text-slate-400 hover:text-slate-900"
                data-testid="gw-ou-panel-close" aria-label="Close panel">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-3 py-2 text-[11px] text-slate-600 flex items-center gap-3 border-b border-slate-100">
        <span>{ou.user_count} user{ou.user_count !== 1 ? 's' : ''}</span>
        <span className="text-slate-300">·</span>
        <span>{ou.findings_count || 0} active finding{(ou.findings_count || 0) !== 1 ? 's' : ''}</span>
        {ou.worst_severity && (
          <>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1">
              worst
              <span className={`inline-block w-2 h-2 rounded-full ${SEV_DOT_CLASSES[ou.worst_severity]}`} />
              {ou.worst_severity}
            </span>
          </>
        )}
      </div>

      <div className="px-3 py-2 max-h-[420px] overflow-y-auto">
        {findings.length === 0 && (
          <p className="text-xs text-slate-500 py-3 text-center" data-testid="gw-ou-panel-clean">
            No active findings affecting this OU. 🎉
          </p>
        )}
        {findings.length > 0 && (
          <ul className="space-y-1.5" data-testid="gw-ou-panel-findings">
            {findings.map(f => (
              <li key={f.rule_key}
                  className={`text-xs border rounded px-2 py-1.5 flex items-start gap-2 ${
                    f.severity === 'critical' ? 'border-l-[4px] border-l-red-600 border-red-200 bg-red-50/40' :
                    f.severity === 'high' ? 'border-l-[3px] border-l-orange-500 border-orange-200' :
                    'border-slate-200'
                  }`}
                  data-testid={`gw-ou-panel-finding-${f.rule_key}`}>
                <span className={`inline-block w-2 h-2 rounded-full mt-1 ${SEV_DOT_CLASSES[f.severity] || ''}`} />
                <span className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 truncate">{f.label || f.rule_key}</div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">{f.severity}</div>
                </span>
              </li>
            ))}
          </ul>
        )}

        {kids.length > 0 && (
          <div className="mt-3 pt-2 border-t border-slate-100">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Sub-OUs ({kids.length})</p>
            <div className="flex flex-wrap gap-1.5">
              {kids.map(k => (
                <button
                  key={k.org_unit_path}
                  type="button"
                  onClick={() => onSelect(k.org_unit_path)}
                  className={`text-[11px] px-2 py-0.5 rounded border ${
                    k.risky ? 'border-red-200 bg-red-50 text-red-700' : 'border-slate-200 bg-white text-slate-700'
                  } hover:border-slate-400`}
                  data-testid={`gw-ou-panel-sub-${k.source_org_unit_id}`}
                >
                  {k.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// =====================================================================
// Evidence Logs tab — inline list of recent evidence_docs
// =====================================================================

const EVIDENCE_TYPE_TONE = {
  directory_readonly_snapshot: 'bg-blue-50 text-blue-700 border-blue-200',
  google_workspace_directory:  'bg-blue-50 text-blue-700 border-blue-200',
  dwd_probe:                   'bg-indigo-50 text-indigo-700 border-indigo-200',
  google_workspace_dwd:        'bg-indigo-50 text-indigo-700 border-indigo-200',
  policy_snapshot:             'bg-violet-50 text-violet-700 border-violet-200',
  google_workspace_policies:   'bg-violet-50 text-violet-700 border-violet-200',
  default:                     'bg-slate-50 text-slate-700 border-slate-200',
};

const FILTER_TYPES = [
  { key: 'all', label: 'All' },
  { key: 'directory', label: 'Directory sync', match: (et) => /direct/i.test(et || '') },
  { key: 'policy', label: 'Policy snapshot', match: (et) => /polic/i.test(et || '') },
  { key: 'dwd', label: 'DWD probe', match: (et) => /dwd|probe|verification/i.test(et || '') },
];

const EvidenceLogsTab = ({ workspaceId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    // Backend scopes by workspace_id only; the route's user_id segment is
    // a leftover positional and ignored server-side. Fall back to "any" so
    // teammates without their own connection still see workspace evidence.
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    const uid = u?.id || 'any';
    try {
      setLoading(true);
      setErr(null);
      const r = await axios.get(`/api/technologies/evidence-history/${uid}/${workspaceId}`,
        { params: { technology: 'google_workspace', include_data: false } });
      setHistory(r.data?.history || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load evidence');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const friendly = (t) => {
    const key = (t || '').toLowerCase();
    if (key.includes('directory')) return 'Directory Snapshot';
    if (key.includes('posture')) return 'Posture Evaluation';
    if (key.includes('dwd') || key.includes('verification')) return 'DWD Verification';
    if (key.includes('group')) return 'Groups Snapshot';
    if (key.includes('org_unit') || key.includes('ou')) return 'Org Structure Snapshot';
    return (t || 'Evidence').replace(/google_workspace_/g, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const evStatus = (ev) => {
    const s = (ev.status || ev.metadata?.status || 'success').toString().toLowerCase();
    return s.includes('fail') || s.includes('error') ? 'Failed' : 'Success';
  };
  const evWhen = (ev) => ev.collection_timestamp || ev.collected_at;

  return (
    <Card className="border-slate-200" data-testid="gw-evidence-tab">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-500" strokeWidth={1.7} />
            Audit Evidence
          </CardTitle>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="gw-evidence-show-raw-toggle">
              <input type="checkbox" checked={showRaw} onChange={(e) => setShowRaw(e.target.checked)} className="rounded border-slate-300" />
              Show Raw Evidence
            </label>
            <Button onClick={load} variant="ghost" size="sm" disabled={loading} data-testid="gw-evidence-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {err && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{err}</div>
        )}
        {loading && history.length === 0 && (
          <div className="py-6 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading evidence…
          </div>
        )}
        {!loading && history.length === 0 && !err && (
          <div className="py-6 text-center text-sm text-slate-500" data-testid="gw-evidence-empty">
            <FileText className="w-7 h-7 mx-auto text-slate-300 mb-2" />
            No audit evidence collected yet.
          </div>
        )}

        {history.length > 0 && (
          <div className="overflow-x-auto border border-slate-200 rounded-md bg-white" data-testid="gw-evidence-list">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Evidence Type</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  {showRaw && <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Raw</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.slice(0, 50).map((ev, idx) => {
                  const ok = evStatus(ev) === 'Success';
                  return (
                    <tr key={ev._id || idx} className="hover:bg-slate-50" data-testid={`gw-evidence-row-${idx}`}>
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{(() => {
                        const d = parseServerDate(evWhen(ev));
                        return d ? d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
                      })()}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{friendly(ev.evidence_type)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${ok ? 'text-emerald-700' : 'text-red-700'}`}>
                          {ok ? <CheckCircle className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                          {evStatus(ev)}
                        </span>
                      </td>
                      {showRaw && (
                        <td className="px-4 py-3 text-[11px] font-mono text-slate-500">
                          {ev.evidence_type}{ev.compliance_framework && ev.compliance_framework !== 'multiple' ? ` · ${ev.compliance_framework}` : ''}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// =====================================================================
// Settings tab — editable inline workspace settings
// =====================================================================

const SettingsTab = ({ workspaceId, scopesPending }) => {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      const r = await axios.get('/api/workspace-settings/inactive-accounts',
        { params: { workspace_id: workspaceId } });
      setSettings(r.data);
      setDraft({
        inactive_account_threshold_days: r.data?.inactive_account_threshold_days ?? 90,
        never_logged_in_grace_days: r.data?.never_logged_in_grace_days ?? 14,
        super_admin_cap: r.data?.super_admin_cap ?? 4,
      });
    } catch {
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    if (!settings) return false;
    return (
      draft.inactive_account_threshold_days !== (settings.inactive_account_threshold_days ?? 90) ||
      draft.never_logged_in_grace_days !== (settings.never_logged_in_grace_days ?? 14) ||
      draft.super_admin_cap !== (settings.super_admin_cap ?? 4)
    );
  }, [draft, settings]);

  const onSave = async () => {
    try {
      setSaving(true);
      const r = await axios.put('/api/workspace-settings/inactive-accounts',
        draft, { params: { workspace_id: workspaceId } });
      setSettings(r.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    if (!settings) return;
    setDraft({
      inactive_account_threshold_days: settings.inactive_account_threshold_days ?? 90,
      never_logged_in_grace_days: settings.never_logged_in_grace_days ?? 14,
      super_admin_cap: settings.super_admin_cap ?? 4,
    });
  };

  return (
    <div className="space-y-4" data-testid="gw-settings-tab">
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Security Thresholds</CardTitle>
          <CardDescription className="text-xs">
            These limits drive your security checks. Changes apply on the next evaluation.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && !settings && (
            <div className="text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
            </div>
          )}
          {settings && (
            <>
              <SettingField
                label="Inactive account threshold (days)"
                helper="Mark a user as inactive if last_login_at is older than this many days."
                value={draft.inactive_account_threshold_days}
                min={7} max={365}
                onChange={(v) => setDraft(d => ({ ...d, inactive_account_threshold_days: v }))}
                testId="set-inactive-days"
              />
              <SettingField
                label="Never-logged-in grace (days)"
                helper="Allow a freshly provisioned account this many days before flagging it as inactive."
                value={draft.never_logged_in_grace_days}
                min={0} max={90}
                onChange={(v) => setDraft(d => ({ ...d, never_logged_in_grace_days: v }))}
                testId="set-grace-days"
              />
              <SettingField
                label="Super-admin cap"
                helper="Posture is penalised when the number of super_admins exceeds this cap."
                value={draft.super_admin_cap}
                min={1} max={25}
                onChange={(v) => setDraft(d => ({ ...d, super_admin_cap: v }))}
                testId="set-super-cap"
              />

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                <Button variant="ghost" size="sm" onClick={onReset} disabled={!dirty || saving}
                        data-testid="gw-settings-reset-btn">
                  Reset
                </Button>
                <Button size="sm" onClick={onSave} disabled={!dirty || saving}
                        data-testid="gw-settings-save-btn">
                  <Save className="w-3.5 h-3.5 mr-1" />
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Connection</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-900" data-testid="gw-settings-connection-status">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              Google Workspace connected
            </span>
            <Button
              variant="outline" size="sm"
              onClick={() => window.open('https://admin.google.com/ac/owl/domainwidedelegation',
                                         '_blank', 'noopener,noreferrer')}
              data-testid="gw-settings-reconnect-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Reconnect Workspace
            </Button>
          </div>
          {scopesPending && (
            <p className="text-xs text-blue-700 mt-3">
              Scope drift detected — reconnect to unlock the latest security checks.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const SettingField = ({ label, helper, value, min, max, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="min-w-0 flex-1">
      <label className="text-sm font-medium text-slate-800">{label}</label>
      {helper && <p className="text-[11px] text-slate-500 mt-0.5">{helper}</p>}
    </div>
    <Input
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e) => {
        const v = parseInt(e.target.value, 10);
        if (Number.isNaN(v)) return;
        onChange(Math.min(max, Math.max(min, v)));
      }}
      className="w-24 h-8 text-sm text-right"
      data-testid={testId}
    />
  </div>
);


export default GoogleWorkspaceDetail;
